import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal, computed } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';

import { TranslateService } from '@ngx-translate/core';
import {
  interval,
  filter,
  switchMap,
  catchError,
  of,
  take,
  fromEvent,
  merge,
} from 'rxjs';

import { AlertService } from '../alert/alert.service';

interface VersionInfo {
  version: string;
  buildTime: string;
  hash: string;
}

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly http = inject(HttpClient);
  private readonly swUpdate = inject(SwUpdate);
  private readonly alertService = inject(AlertService);
  private readonly translate = inject(TranslateService);

  private readonly isChecking = signal<boolean>(false);
  private readonly lastCheckTime = signal<Date | null>(null);
  private readonly currentVersionInfo = signal<VersionInfo | null>(null);

  readonly isCheckingUpdate = computed(() => this.isChecking());
  readonly lastCheck = computed(() => this.lastCheckTime());
  readonly currentVersion = computed(() => this.currentVersionInfo());

  private readonly CHECK_INTERVAL_MS = 30 * 60 * 1000;
  private readonly VERSION_ENDPOINT = '/version.json';

  startVersionCheck(): void {
    if (!this.isServiceWorkerEnabled()) {
      return;
    }

    this.listenForServiceWorkerUpdates();
    this.performVersionCheck();
    this.setupPeriodicChecks();
    this.setupFocusChecks();
  }

  async manualCheck(): Promise<boolean> {
    return this.performVersionCheck();
  }

  getVersion(): string {
    const versionInfo = this.currentVersionInfo();
    return versionInfo?.version ?? 'unknown';
  }

  private isServiceWorkerEnabled(): boolean {
    return this.swUpdate.isEnabled;
  }

  private async performVersionCheck(): Promise<boolean> {
    if (this.isChecking()) {
      return false;
    }

    this.isChecking.set(true);
    this.lastCheckTime.set(new Date());

    try {
      const timestamp = Date.now();
      const versionInfo = await this.fetchVersionInfo(timestamp);

      if (versionInfo) {
        await this.compareVersions(versionInfo);
      }

      if (this.swUpdate.isEnabled) {
        await this.swUpdate.checkForUpdate();
      }

      return true;
    } catch {
      return false;
    } finally {
      this.isChecking.set(false);
    }
  }

  private fetchVersionInfo(timestamp: number): Promise<VersionInfo | null> {
    return this.http
      .get<VersionInfo>(`${this.VERSION_ENDPOINT}?t=${timestamp}`)
      .pipe(
        take(1),
        catchError(() => of(null)),
      )
      .toPromise()
      .then((result) => result ?? null);
  }

  private async compareVersions(newVersion: VersionInfo): Promise<void> {
    const current = this.currentVersionInfo();

    if (!current) {
      this.currentVersionInfo.set(newVersion);
      return;
    }

    const hasChanged =
      current.version !== newVersion.version ||
      current.hash !== newVersion.hash;

    if (hasChanged) {
      this.notifyNewVersion();
    }
  }

  private listenForServiceWorkerUpdates(): void {
    this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
      )
      .subscribe(() => {
        this.notifyNewVersion();
      });
  }

  private setupPeriodicChecks(): void {
    interval(this.CHECK_INTERVAL_MS)
      .pipe(
        switchMap(() => this.performVersionCheck()),
        catchError(() => of(false)),
      )
      .subscribe();
  }

  private setupFocusChecks(): void {
    if (typeof window === 'undefined') return;

    const focus$ = fromEvent(window, 'focus');
    const visibilityChange$ = fromEvent(document, 'visibilitychange');

    merge(focus$, visibilityChange$)
      .pipe(
        filter(() => !document.hidden),
        switchMap(() => this.performVersionCheck()),
        catchError(() => of(false)),
      )
      .subscribe();
  }

  private notifyNewVersion(): void {
    this.alertService
      .open({
        title: this.translate.instant('app.update.title'),
        message: this.translate.instant('app.update.message'),
        type: 'info',
        acceptText: this.translate.instant('app.update.accept'),
        rejectText: this.translate.instant('app.update.reject'),
        showIcon: true,
      })
      .subscribe({
        accept: () => this.activateUpdate(),
      });
  }

  private async activateUpdate(): Promise<void> {
    try {
      if (this.swUpdate.isEnabled) {
        await this.swUpdate.activateUpdate();
      }

      await this.clearAllCaches();
      window.location.reload();
    } catch {
      window.location.reload();
    }
  }

  private async clearAllCaches(): Promise<void> {
    if (!('caches' in window)) {
      return;
    }

    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    } catch (error) {
      console.warn('Failed to clear caches:', error);
    }
  }
}
